import { useMemo, useRef, useEffect } from 'react';
import { Bar } from 'react-chartjs-2';
import { ChartData, ChartOptions } from 'chart.js';
import { ChartComponentProps, BarChartHookReturn } from './types';
import {
  handleSegmentClick,
  handleLabelClick,
  getRecursiveSelection,
} from './utils';
import { generateStackedChartData } from './dataUtils';
import { createChartOptions } from './chartConfig';
import { AggregatedOrderViewType } from 'src/gql/graphql';

export const useStackedBarChart = ({
  table,
  grouping,
  rowSelection,
  setRowSelection,
  borderColor,
  textColor,
}: ChartComponentProps): BarChartHookReturn => {
  const chartRef = useRef<any>(null);

  // Add custom click listener for x-axis labels
  useEffect(() => {
    const handleCanvasClick = (event: MouseEvent) => {
      if (chartRef.current) {
        const chart = chartRef.current;
        const rect = chart.canvas.getBoundingClientRect();
        const canvasPosition = {
          x: event.clientX - rect.left,
          y: event.clientY - rect.top,
        };
        const dataX = chart.scales.x.getValueForPixel(canvasPosition.x);

        // Check if click is in the x-axis label area (below the chart)
        const chartArea = chart.chartArea;
        const isInLabelArea = canvasPosition.y > chartArea.bottom;

        if (
          isInLabelArea &&
          dataX !== null &&
          dataX !== undefined &&
          dataX >= 0
        ) {
          const dataIndex = Math.round(dataX);
          const tableRows = table.getRowModel().rows;

          if (dataIndex < tableRows.length) {
            const parentRow = tableRows[dataIndex];
            const shiftPressed = event.shiftKey;

            if (shiftPressed) {
              const isCurrentlySelected =
                table.getState().rowSelection[parentRow.id] ?? false;
              if (isCurrentlySelected) {
                const entriesToRemove = Object.fromEntries(
                  getRecursiveSelection(parentRow).map(([id]) => [id, false])
                );
                setRowSelection((prev) => ({ ...prev, ...entriesToRemove }));
              } else {
                const entriesToAdd = Object.fromEntries(
                  getRecursiveSelection(parentRow)
                );
                setRowSelection((prev) => ({ ...prev, ...entriesToAdd }));
              }
            } else {
              const entries = Object.fromEntries(
                getRecursiveSelection(parentRow)
              );
              setRowSelection(entries);
            }
          }
        }
      }
    };

    const canvas = chartRef.current?.canvas;
    if (canvas) {
      canvas.addEventListener('click', handleCanvasClick);
      return () => {
        canvas.removeEventListener('click', handleCanvasClick);
      };
    }
  }, [table, setRowSelection]);

  const chartData = useMemo(() => {
    return generateStackedChartData(
      table,
      grouping,
      rowSelection,
      AggregatedOrderViewType.StackedBarChart
    ) as ChartData<'bar'>;
  }, [
    table.options.data,
    table.getState().columnFilters,
    table.getState().grouping,
    grouping,
    rowSelection,
  ]);

  const chartOptions = useMemo((): ChartOptions<'bar'> => {
    return createChartOptions(
      AggregatedOrderViewType.StackedBarChart,
      borderColor,
      textColor,
      table,
      rowSelection,
      setRowSelection,
      handleSegmentClick,
      handleLabelClick
    ) as ChartOptions<'bar'>;
  }, [
    borderColor,
    textColor,
    table.options.data,
    table.getState().columnFilters,
    table.getState().grouping,
    rowSelection,
    setRowSelection,
  ]);

  return {
    chartData,
    chartOptions,
    chartRef,
  };
};

export default function StackedBarChart(props: ChartComponentProps) {
  const { chartData, chartOptions, chartRef } = useStackedBarChart(props);

  return <Bar data={chartData} options={chartOptions} ref={chartRef as any} />;
}
